import type { IntellioAssistantConversationSessionRecord } from '@triedge/domain-core';
import { selectPreferredConversationSessionId } from './assistantConversationSessionRouting';

export const buildFallbackAssistantTaskLaunchSessionId = (draftId: string): string => (
  `task-launch:${draftId}`
);

export const resolveAssistantTaskLaunchOriginSessionId = (input: {
  draftId: string;
  currentConversationSessionId: string | null;
  activeConversationSessionId: string | null;
  conversationSessions: IntellioAssistantConversationSessionRecord[];
  selectedThreadsSessionId: string | null;
  pathname: string;
  moduleId: string;
}): string => {
  const directSessionId = input.currentConversationSessionId ?? input.activeConversationSessionId;
  if (directSessionId) {
    return directSessionId;
  }

  if (input.conversationSessions.length > 0) {
    const preferredSessionId = selectPreferredConversationSessionId({
      sessions: input.conversationSessions,
      currentConversationSessionId: input.selectedThreadsSessionId,
      pathname: input.pathname,
      moduleId: input.moduleId,
    });
    if (preferredSessionId) {
      return preferredSessionId;
    }
  }

  // Background task starts must not block on durable-session warmup.
  return buildFallbackAssistantTaskLaunchSessionId(input.draftId);
};

export const resolveAssistantTaskLaunchOriginSessionIdForStart = async (input: {
  draftId: string;
  currentConversationSessionId: string | null;
  activeConversationSessionId: string | null;
  conversationSessions: IntellioAssistantConversationSessionRecord[];
  selectedThreadsSessionId: string | null;
  pathname: string;
  moduleId: string;
  pendingSessionPromise?: Promise<IntellioAssistantConversationSessionRecord | null> | null;
  startFreshConversationSession?: (() => Promise<IntellioAssistantConversationSessionRecord | null>) | null;
}): Promise<string> => {
  const fallbackSessionId = buildFallbackAssistantTaskLaunchSessionId(input.draftId);
  const resolvedSessionId = resolveAssistantTaskLaunchOriginSessionId(input);
  if (resolvedSessionId !== fallbackSessionId) {
    return resolvedSessionId;
  }

  const warmedSession = input.pendingSessionPromise
    ? await input.pendingSessionPromise.catch(() => null)
    : input.startFreshConversationSession
      ? await input.startFreshConversationSession().catch(() => null)
      : null;

  return typeof warmedSession?.id === 'string' && warmedSession.id.trim().length > 0
    ? warmedSession.id
    : fallbackSessionId;
};
